import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgForm, NgModel } from '@angular/forms';
import { Observable } from 'rxjs';
import { delay, map, startWith } from 'rxjs/operators';

import {
    AnnualNetIncome,
    ContractualTime,
    EmploymentSituation,
    ActivityWithoutIncomeSource,
    GetProfessionalPositionsUseCase,
    GetOtherProfessionalPositionsUseCase,
    ProfessionalPositionInterface,
    OtherProfessionalPositionInterface,
    SourceFunds,
    SourceIncome,
} from '../../../../core';
import { BaseUI } from '../../base.ui';
import { ErrorDialog } from '../../dialog/error/error.dialog';
import { FullClient, ProfessionalPosition, OtherProfessionalPosition } from '../../../domain/data';
import { ParentEvents } from '../../../utils/parent-events.service';
import { ParentFormDirective } from '../../../directives/parent-form.directive';

@Component({
    selector: 'thr-full-data-two-natural-person',
    templateUrl: './full-data-two-natural-person.component.html',
    styleUrls: ['./full-data-two-natural-person.component.scss'],
})
export class FullDataTwoNaturalPersonComponent extends BaseUI implements AfterViewInit, OnDestroy, OnInit {
    public AnnualNetIncome: typeof AnnualNetIncome = AnnualNetIncome;
    public ContractualTime: typeof ContractualTime = ContractualTime;
    public EmploymentSituation: typeof EmploymentSituation = EmploymentSituation;
    public ActivityWithoutIncomeSource: typeof ActivityWithoutIncomeSource = ActivityWithoutIncomeSource;
    public SourceFunds: typeof SourceFunds = SourceFunds;
    public SourceIncome: typeof SourceIncome = SourceIncome;
    public form: NgForm;
    public isFormValid = false;
    public position: string | ProfessionalPositionInterface;
    public positions: ProfessionalPositionInterface[];
    public positionsFiltered: Observable<ProfessionalPositionInterface[]>;
    public otherPosition: boolean | OtherProfessionalPositionInterface;
    public otherPositions: OtherProfessionalPositionInterface[];
    public activityWithoutIncomeSource = undefined;
    public activityWithoutIncome: boolean;
    public showActivity: boolean = undefined;
    public showActivityIncomeNo: boolean = undefined;
    public otherActivity: boolean;
    public activityExist: boolean = undefined;
    public salary: boolean = undefined;

    @Input()
    client: FullClient;

    @Input()
    isDisabled = false;

    @Input()
    isDisabledSubmit = false;

    @ViewChild('formDirective')
    formDirective: ParentFormDirective;

    @ViewChild('positionCtrl')
    positionCtrl: NgModel;

    constructor(
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private getProfessionalPositionsUseCase: GetProfessionalPositionsUseCase,
        private getOtherProfessionalPositionsUseCase: GetOtherProfessionalPositionsUseCase
    ) {
        super(parentEvents);
    }

    get isEmployeeOrSelfEmployed(): boolean {
        return [EmploymentSituation.AS_EMPLOYEE, EmploymentSituation.SELF_EMPLOYED].includes(this.client?.professional.employmentSituation);
    }

    ngOnInit() {
        this.getProfessionalPositions();
        this.getOtherProfessionalPositions();
        if (this.client.professional.activityWithoutIncome == null) {
            this.activityExist = undefined;
        }
        if (!!this.client.professional.activityWithoutIncome || !!this.client.professional.employmentSituation) {
            this.showActivity = true;
            if (!!this.client.professional.activityWithoutIncome) {
                this.activityExist = true;
                this.showActivityIncomeNo = true;
            } else {
                this.activityExist = false;
                this.showActivityIncomeNo = false;
            }
        }
    }

    private getProfessionalPositions() {
        this.getProfessionalPositionsUseCase
            .bind()
            .pipe(delay(1)) // prevent ExpressionChangedAfterItHasBeenCheckedError
            .subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.positions = transaction.data;
                    if (this.client.professional.position) {
                        this.positions.map((position) => {
                            if (String(position.id) === this.client.professional.position) {
                                this.position = position;
                            }
                        });
                    }

                    this.positionsFiltered = this.positionCtrl.valueChanges.pipe(
                        startWith(''),
                        map((value) => this.filterPositions(value))
                    );
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog);
                }
            });
    }

    private getOtherProfessionalPositions() {
        this.getOtherProfessionalPositionsUseCase
            .bind()
            .pipe(delay(1)) // prevent ExpressionChangedAfterItHasBeenCheckedError
            .subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.otherPositions = transaction.data;
                    /*if (this.client.professional.position) {
                        this.positions.map((position) => {
                            if (String(position.id) === this.client.professional.position) {
                                this.position = position;
                            }
                        });
                    }*/
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog);
                }
            });
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.form = this.formDirective.controlContainer as NgForm;

        if (this.form) {
            this.form.statusChanges.subscribe(() => {
                setTimeout(() => {
                    this.isFormValid = this.form.valid;
                });
            });
        }
    }

    private filterPositions(value: string | ProfessionalPosition): ProfessionalPositionInterface[] {
        if (value) {
            if (value instanceof ProfessionalPosition) {
                return this.positions.filter((position) => String(position.id) === String(value.id));
            }

            return this.positions.filter(
                (position) =>
                    this.removeAccents(position.name.toLowerCase()).indexOf(this.removeAccents(value.toLowerCase())) !== -1 ||
                    this.removeAccents(position.code.toLowerCase()).indexOf(this.removeAccents(value.toLowerCase())) === 0
            );
        }

        return this.positions;
    }

    private removeAccents(value) {
        return value.toString().replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i').replace(/ó/g, 'o').replace(/ú/g, 'u');
    }

    onResize() {
        this.parentEvents.resize();
    }

    onPositionChange() {
        if (this.position instanceof ProfessionalPosition) {
            this.client.professional.position = String(this.position.id);
        }
    }

    cleanForm() {
        if (this.form.controls.fdtnpCompanyName) {
            this.client.professional.companyName = null;
            this.form.controls.fdtnpCompanyName.reset();
            this.form.controls.fdtnpCompanyName.updateValueAndValidity();
        }
        if (this.form.controls.fdtnpCompanyActivity) {
            this.client.professional.companyActivity = null;
            this.form.controls.fdtnpCompanyActivity.reset();
            this.form.controls.fdtnpCompanyActivity.updateValueAndValidity();
        }
        if (this.form.controls.fdtnpPosition) {
            this.client.professional.position = null;
            this.form.controls.fdtnpPosition.reset();
            this.form.controls.fdtnpPosition.updateValueAndValidity();
        }
        if (this.form.controls.fdtnpContractualTime) {
            this.client.professional.contractualTime = null;
            this.form.controls.fdtnpContractualTime.reset();
            this.form.controls.fdtnpContractualTime.updateValueAndValidity();
        }
        if (this.form.controls.fdtnpYesNo) {
            this.form.controls.fdtnpYesNo.reset();
            this.form.controls.fdtnpYesNo.updateValueAndValidity();
        }
        if (this.form.controls.fdtnpSourceFunds) {
            this.client.professional.sourceOfFunds = null;
            this.form.controls.fdtnpSourceFunds.reset();
            this.form.controls.fdtnpSourceFunds.updateValueAndValidity();
        }
        if (this.form.controls.fdtnpSourceIncome) {
            this.client.professional.sourceOfIncome = null;
            this.form.controls.fdtnpSourceIncome.reset();
            this.form.controls.fdtnpSourceIncome.updateValueAndValidity();
        }
        if (this.form.controls.fdtnpOtherActivity) {
            this.form.controls.fdtnpOtherActivity.reset();
            this.form.controls.fdtnpOtherActivity.updateValueAndValidity();
        }
        if (this.form.controls.fdtnpOtherActivitySource) {
            this.client.professional.riskActivityCode = null;
            this.form.controls.fdtnpOtherActivitySource.reset();
            this.form.controls.fdtnpOtherActivitySource.updateValueAndValidity();
        }
        if (this.form.controls.fdtnpAnnualNet) {
            this.client.professional.annualNetIncome = null;
            this.form.controls.fdtnpAnnualNet.reset();
            this.form.controls.fdtnpAnnualNet.updateValueAndValidity();
        }
    }

    onYesNoChange() {
        if (this.form.controls.fdtnpAdditionalActivitiesInfo) {
            this.client.professional.additionalActivitiesInfo = null;
            this.form.controls.fdtnpAdditionalActivitiesInfo.reset();
            this.form.controls.fdtnpAdditionalActivitiesInfo.updateValueAndValidity();
        }
    }

    onSourceFundsChange() {
        if (this.form.controls.fdtnpSourceFundsOther) {
            this.form.controls.fdtnpSourceFundsOther.reset();
            this.form.controls.fdtnpSourceFundsOther.updateValueAndValidity();
        }
    }

    onOtherActivityChange(value) {
        if (this.form.controls.fdtnpOtherActivitySource) {
            this.form.controls.fdtnpOtherActivitySource.reset();
            this.form.controls.fdtnpOtherActivitySource.updateValueAndValidity();
        }
        this.client.professional.hasRiskActivity = value;
    }

    onActivityChange(value) {
        this.showActivity = true;
        this.cleanForm()

        if (value == true) {
            this.form.controls.fdtnpSituation.setValue(EmploymentSituation.UNPAID_ACTIVITY);
            this.form.controls.fdtnpSituation.updateValueAndValidity();
            this.salary = false;
            this.showActivityIncomeNo = true;
        }
        if (value == false) {
            this.client.professional.employmentSituation = undefined;
            this.salary = true;
            this.showActivityIncomeNo = false;
        }
        this.showActivityIncomeNo = value;
    }
}
